import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, TrendingUp, Users, BarChart3 } from 'lucide-react';
import Header from '../layout/Header';
import LeadersSection from '../components/LeadersSection';
import AikupCard from '../components/AikupCard';
import type { AikupItem } from '../data/aikupData';
import { useAuth } from '../contexts/AuthContext';
import GouvernanceLoi27Modal from '../components/GouvernanceLoi27Modal';

interface RHItem {
  id: number;
  title: string;
  category: string;
  description: string;
  aiSuggestion: string;
  kpiScore: number;
  imageUrl?: string;
}

const comiteRHData: RHItem[] = [
  {
    id: 1,
    title: 'Gouvernance — Loi 27 (LMRSST)',
    category: 'Gouvernance SST',
    description:
      'Cadre de gouvernance du comité SST selon la Loi modernisant le régime de santé et de sécurité du travail. Structure, RACI, programme de prévention, risques psychosociaux (RPS) et conformité CNESST pour BZ (55 employés).',
    aiSuggestion:
      'Carte structurante : donne le ton et montre que BZ est organisée. Référence pour le comité SST paritaire, le représentant SST, les rituels, la roadmap de conformité et les liens vers les sources officielles (CNESST, Loi 27).',
    kpiScore: 95,
    imageUrl: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&auto=format&fit=crop&q=80',
  },
];

export default function ComiteRH() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isGouvernanceLoi27Open, setIsGouvernanceLoi27Open] = useState(false);

  const emailsAutorisesRH = useMemo(
    () => [
      'dany.bourassa@bzinc.ca',
      'melissa.pelletier@bzinc.ca',
      'emilie.quintal@bzinc.ca',
      'louis-alexandre.hebert@bzinc.ca',
      'eric.durand@bzinc.ca',
    ],
    []
  );

  const estAutorise = user?.email && emailsAutorisesRH.includes(user.email.toLowerCase());

  useEffect(() => {
    if (!user?.email) {
      navigate('/comites');
      return;
    }
    if (!estAutorise) {
      navigate('/comites');
    }
  }, [user, navigate, estAutorise]);

  if (!user?.email) return null;
  if (!estAutorise) return null;

  const AVATAR_YM = '/DB.png';
  const AVATAR_MP = '/MP.png';
  const AVATAR_GC = '/GC.png';
  const leaders = [
    {
      name: 'Dany Bourassa',
      title: 'CA/CE • Owner comité RH / Loi 27',
      avatar: AVATAR_YM,
      vision:
        'Le comité RH et la conformité Loi 27 existent pour donner le ton : une organisation structurée, qui prend au sérieux la santé et la sécurité du travail (physique et psychologique) et qui documente ses actions.',
      attentes:
        'Décisions tracées, comité SST opérationnel, programme de prévention à jour et risques psychosociaux intégrés. BZ doit montrer qu’elle est conforme et qu’elle a commencé.',
      pagePRIsME:
        'Cette page est le pilier gouvernance Loi 27 : cadre, RACI, roadmap, rituels et références officielles.',
    },
    {
      name: 'Mélissa Pelletier',
      title: 'RH • Responsable opérationnelle Loi 27',
      avatar: AVATAR_MP,
      vision:
        'Mélissa pilote l’opérationnel : désignation du représentant SST, préparation des réunions du comité SST, registres, formation et suivi des actions de prévention.',
      attentes:
        'Procès-verbaux conformes, politique et programme de prévention documentés, formation employés et gestionnaires, et preuves pour la CNESST.',
      pagePRIsME:
        'La carte Gouvernance Loi 27 centralise le cadre pour que tout le monde s’aligne sur les mêmes bases.',
    },
    {
      name: 'Guillaume Cloutier',
      title: 'Membre comité RH & Loi 27',
      avatar: AVATAR_GC,
      vision:
        'Membre du comité RH & Loi 27, contributeur à la gouvernance SST et à la conformité.',
      attentes:
        'Participer aux orientations et au suivi du cadre Loi 27.',
      pagePRIsME:
        'Cette page et la carte Gouvernance Loi 27 regroupent le cadre et les références du comité.',
    },
  ];

  const totalItems = comiteRHData.length;
  const avgKpiScore = Math.round(comiteRHData.reduce((sum, item) => sum + item.kpiScore, 0) / totalItems);
  const categories = Array.from(new Set(comiteRHData.map((item) => item.category)));
  const highPriorityItems = comiteRHData.filter((item) => item.kpiScore >= 90).length;

  const handleCardClick = (item: RHItem) => {
    if (item.id === 1) setIsGouvernanceLoi27Open(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header
        title="Comité RH • Loi 27"
        subtitle="Gouvernance SST, conformité Loi 27 (LMRSST), comité SST paritaire, risques psychosociaux et équité. Donner le ton et montrer qu'on est organisés."
      />

      <div className="max-w-7xl mx-auto px-8 pb-12">
        <LeadersSection leaders={leaders} title="Membres du comité RH & Loi 27" />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-rose-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-rose-600" />
              <p className="text-sm text-gray-600">Outils disponibles</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalItems}</p>
            <p className="text-xs text-gray-500 mt-1">Gouvernance & réunions</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <p className="text-sm text-gray-600">Score excellence</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{avgKpiScore}%</p>
            <p className="text-xs text-gray-500 mt-1">Moyenne KPI</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-pink-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <CalendarCheck className="w-5 h-5 text-pink-600" />
              <p className="text-sm text-gray-600">Priorités</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{highPriorityItems}</p>
            <p className="text-xs text-gray-500 mt-1">Dossiers 90%+</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-rose-400"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-rose-600" />
              <p className="text-sm text-gray-600">Domaines</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{categories.length}</p>
            <p className="text-xs text-gray-500 mt-1">Catégories couvertes</p>
          </motion.div>
        </div>

        {categories.map((category, categoryIndex) => {
          const itemsInCategory = comiteRHData.filter((item) => item.category === category);
          return (
            <div key={category} className="mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="flex-1 h-px bg-gradient-to-r from-rose-500 to-pink-500" />
                <h2 className="text-2xl font-bold text-gray-800 px-4">{category}</h2>
                <div className="flex-1 h-px bg-gradient-to-l from-rose-500 to-pink-500" />
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {itemsInCategory.map((item, idx) => {
                  const aikupItem: AikupItem = {
                    id: item.id,
                    title: item.title,
                    category: item.category,
                    description: item.description,
                    aiSuggestion: item.aiSuggestion,
                    kpiScore: item.kpiScore,
                    imageUrl: item.imageUrl,
                  };
                  const gouvernanceCardLeaders =
                    item.id === 1
                      ? [
                          { name: 'Dany Bourassa', avatar: AVATAR_YM, title: 'Owner comité RH / Loi 27' },
                          { name: 'Mélissa Pelletier', avatar: AVATAR_MP, title: 'Responsable opérationnelle Loi 27' },
                          { name: 'Guillaume Cloutier', avatar: AVATAR_GC, title: 'Membre comité RH & Loi 27' },
                        ]
                      : undefined;
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: categoryIndex * 0.1 + idx * 0.05 }}
                    >
                      <AikupCard
                        item={aikupItem}
                        onClick={() => handleCardClick(item)}
                        onExpertClick={() => {}}
                        index={idx}
                        customLeaders={gouvernanceCardLeaders}
                      />
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <GouvernanceLoi27Modal
        isOpen={isGouvernanceLoi27Open}
        onClose={() => setIsGouvernanceLoi27Open(false)}
      />
    </div>
  );
}
